import { env } from "../../env";
import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from "antd";
import { DownloadOutlined } from '@ant-design/icons';

const CSVDownload = ({ resultsId }) => {
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [eventSource, setEventSource] = useState(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    return () => {
      if (eventSource) {
        console.log('Cleaning up EventSource');
        eventSource.close();
      }
    };
  }, [eventSource]);

  const startDownload = async () => {
    try {
      setIsDownloading(true);
      setError(null);
      setProgress(null);

      // Generate a unique session ID for this download
      const sessionId = Date.now().toString() + resultsId;

      // Start the SSE connection for progress updates
      const sessionResponse = await fetch(`${env.BACKEND}/api/create-session/${resultsId}/${sessionId}`, {
        method: 'POST'
      });

      if (!sessionResponse.ok) {
        const error = await sessionResponse.json();
        throw new Error(error.message || 'Failed to create session');
      }
      console.log('Session created:', sessionId);

      const newEventSource = new EventSource(`${env.BACKEND}/api/progress/${sessionId}`);
      setEventSource(newEventSource);

      newEventSource.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.error) {
          console.error('Error in progress event:', data.error);
          setError(data.error);
          newEventSource.close();
          setIsDownloading(false);
        } else {
          console.log('Updating progress:', data);
          setProgress(data);
          if (data.completed) {
            newEventSource.close();
            setIsDownloading(false);
          }
        }
      };

      newEventSource.onerror = () => {
        setError('Lost connection to server');
        newEventSource.close();
        setIsDownloading(false);
      };

      // Start the actual download
      const downloadUrl = `${env.BACKEND}/api/download/${resultsId}/${sessionId}`;
      if (iframeRef.current) {
        iframeRef.current.src = downloadUrl;
      }
    } catch (error) {
      console.error('Download process error:', error);
      setError(error.message || 'Failed to start download');
      setIsDownloading(false);
    }
  };

  return (
    <>
      <iframe
        ref={iframeRef}
        style={{ display: 'none' }}
        title="download-frame"
      />
      <Button
        type="primary"
        shape="round"
        icon={<DownloadOutlined />}
        size="large"
        onClick={startDownload}
        disabled={isDownloading}
        className={`
        ${isDownloading
            ? 'bg-gray-200 cursor-not-allowed'
            : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
      >
        <b> {isDownloading ? 'Downloading...' : 'Download CSV'}</b>
      </Button>

      {isDownloading && progress && (
        <div className="mt-4">
          <div className="mb-2 flex justify-between text-sm">
            <span>Downloading...</span>
            <span>{progress.percentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
          {progress && (
            <div className="mt-1 text-sm text-gray-600">
              {progress.processed?.toLocaleString()} of {progress.total?.toLocaleString()} records
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {progress?.completed && (
        <div className="mt-4 p-3 bg-green-100 text-green-700 rounded-lg flex items-center gap-2">
          <CheckCircle size={20} />
          Download completed!
        </div>
      )}
    </>
  );
};

export default CSVDownload;